import React, { useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { usePortfolio } from "../context/PortfolioContext";
import { fmtINR, fmtPct, shortDate, longDate } from "../lib/format";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const RANGES = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1Y", days: 365 },
  { key: "ALL", days: 99999 },
];

function filterByRange(history, days) {
  if (!history.length) return [];
  if (days >= 99999) return history;
  // history is sorted ASC by date. Cut off entries older than (latest - days)
  const latest = new Date(history[history.length - 1].date);
  const cutoff = new Date(latest);
  cutoff.setDate(cutoff.getDate() - days);
  return history.filter((d) => new Date(d.date) >= cutoff);
}

export default function NavHistory() {
  const { navHistory, summary, loading, reload } = usePortfolio();
  const { user } = useAuth();
  const { toast } = useToast();
  const [range, setRange] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    nav: "",
    note: "",
  });

  const data = useMemo(
    () => filterByRange(navHistory, RANGES.find((r) => r.key === range).days),
    [range, navHistory]
  );

  // Full descending list for the "All recorded NAVs" table (newest first)
  const allDesc = useMemo(
    () => [...navHistory].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [navHistory]
  );

  const exportData = useMemo(() => {
    const headers = ["Date", "NAV", "Member", "Type", "Funds", "Units"];
    const rows = allDesc.map((d) => [
      d.date, d.nav, d.memberName || "", d.type || "", d.funds || "", d.units || "",
    ]);
    const pdfColumns = ["Date", "NAV", "Member", "Type", "Funds", "Units"];
    const pdfRows = allDesc.map((d) => [
      d.date,
      "₹" + Number(d.nav || 0).toFixed(4),
      d.memberName || "—",
      d.type || "—",
      d.funds ? fmtINR(Number(d.funds)) : "—",
      d.units ? Number(d.units).toFixed(4) : "—",
    ]);
    return { headers, rows, pdfColumns, pdfRows };
  }, [allDesc]);

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );

  const first = data[0]?.nav || 0;
  const last = data[data.length - 1]?.nav || 0;
  const change = last - first;
  const changePct = first > 0 ? (change / first) * 100 : 0;
  const isAdmin = user?.role === "admin";

  const handleSaveDailyNav = async (e) => {
    e?.preventDefault?.();
    const nav = parseFloat(form.nav);
    if (!form.date || !nav || nav <= 0) {
      toast({ title: "Invalid input", description: "Enter a valid date and NAV (> 0)." });
      return;
    }
    setSaving(true);
    try {
      await api.upsertDailyNav({ date: form.date, nav, note: form.note || "" });
      toast({ title: "NAV recorded", description: `₹${nav.toFixed(4)} on ${form.date}` });
      setForm({ date: new Date().toISOString().slice(0, 10), nav: "", note: "" });
      setShowForm(false);
      await reload();
    } catch (err) {
      toast({
        title: "Save failed",
        description: err.response?.data?.detail || "Could not record NAV.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDailyNav = async (dateISO) => {
    if (!window.confirm(`Remove the manually recorded NAV for ${dateISO}?`)) return;
    try {
      await api.deleteDailyNav(dateISO);
      toast({ title: "NAV removed", description: dateISO });
      await reload();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err.response?.data?.detail || "Could not remove NAV.",
      });
    }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            NAV History
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight tabular-nums">
            ₹{(summary?.currentNAV || 0).toFixed(4)}
          </h1>
          <div className={`text-sm font-mono mt-1 ${change >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {change >= 0 ? "+" : ""}₹{change.toFixed(4)} ({fmtPct(changePct)}) · {range}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button
              type="button"
              data-testid="record-nav-toggle-btn"
              onClick={() => setShowForm((s) => !s)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-500 text-slate-950 hover:bg-emerald-400 transition-colors"
            >
              {showForm ? <X size={14} /> : <Plus size={14} />}
              {showForm ? "Close" : "Record NAV"}
            </button>
          )}
          <ExportButtons
            testIdPrefix="nav-history-export"
            disabled={allDesc.length === 0}
            csv={{
              filename: stampedName("nav_history"),
              headers: exportData.headers,
              rows: exportData.rows,
            }}
            pdf={{
              title: "NAV history",
              subtitle: `${allDesc.length} entries · Current NAV ₹${(summary?.currentNAV || 0).toFixed(4)}`,
              filename: "nav_history",
              columns: exportData.pdfColumns,
              rows: exportData.pdfRows,
            }}
          />
        </div>
      </div>

      {isAdmin && showForm && (
        <form
          onSubmit={handleSaveDailyNav}
          data-testid="record-nav-form"
          className="mt-4 bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-wrap items-end gap-3"
        >
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Date</label>
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="record-nav-date-input"
              max={new Date().toISOString().slice(0, 10)}
              className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">NAV (₹)</label>
            <input
              type="number"
              step="0.0001"
              min="0"
              required
              placeholder="e.g. 112.3456"
              value={form.nav}
              onChange={(e) => setForm((f) => ({ ...f, nav: e.target.value }))}
              data-testid="record-nav-value-input"
              className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 w-40 font-mono"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Note (optional)</label>
            <input
              type="text"
              placeholder="e.g. EOD snapshot"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="record-nav-note-input"
              className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="record-nav-save-btn"
            className="px-4 py-2 rounded-md text-xs font-semibold bg-emerald-500 text-slate-950 hover:bg-emerald-400 disabled:opacity-60 transition-colors"
          >
            {saving ? "Saving…" : "Save NAV"}
          </button>
          <div className="basis-full text-[11px] text-slate-500">
            Recording a NAV for a date upserts (one snapshot per day). It appears instantly in the chart above, the list below, and the Dashboard NAV Trend.
          </div>
        </form>
      )}

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="nav-history-range-filter">
        {RANGES.map((r) => (
          <button
            key={r.key}
            data-testid={`nav-range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              range === r.key
                ? "bg-emerald-500 text-slate-950 border-emerald-500"
                : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"
            }`}
          >
            {r.key}
          </button>
        ))}
      </div>

      <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl p-3 md:p-5">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="nf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                stroke="#475569"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
              />
              <YAxis
                stroke="#475569"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                domain={["dataMin - 1", "dataMax + 1"]}
                tickFormatter={(v) => v.toFixed(2)}
                width={46}
              />
              <Tooltip
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#e2e8f0",
                }}
                formatter={(v) => [`\u20b9${Number(v).toFixed(4)}`, "NAV"]}
                labelFormatter={longDate}
              />
              <Area type="monotone" dataKey="nav" stroke="#10b981" strokeWidth={2} fill="url(#nf)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="nav-history-full-list">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="text-sm font-semibold text-white">All recorded NAVs</div>
          <div className="text-xs text-slate-500">
            {allDesc.length} {allDesc.length === 1 ? "entry" : "entries"}
          </div>
        </div>
        <div className="divide-y divide-slate-800 max-h-[560px] overflow-y-auto">
          {allDesc.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center">No NAV entries yet.</div>
          )}
          {allDesc.map((d, i) => {
            const next = allDesc[i + 1]; // older entry (since list is desc)
            const diff = next ? d.nav - next.nav : 0;
            const isLive = d.type === "live";
            const isDaily = d.type === "daily";
            return (
              <div
                key={(d.txId || d.type || "row") + "-" + d.date + "-" + i}
                data-testid={`nav-row-${d.date}`}
                className={`px-4 py-2.5 flex items-center justify-between gap-3 text-sm ${isLive ? "bg-emerald-500/5" : ""} ${isDaily ? "bg-sky-500/5" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-slate-300">
                    {longDate(d.date)}
                    {isLive && <span className="ml-2 text-[10px] text-emerald-400 uppercase tracking-wider">Live</span>}
                    {isDaily && <span className="ml-2 text-[10px] text-sky-400 uppercase tracking-wider">Manual</span>}
                  </div>
                  {d.memberName && (
                    <div className="text-[11px] text-slate-500 truncate">
                      {d.memberName}{d.funds ? ` · ${fmtINR(d.funds)}` : ""}{d.units ? ` · ${d.units.toFixed(4)} u` : ""}
                    </div>
                  )}
                  {isDaily && d.note && (
                    <div className="text-[11px] text-slate-500 truncate">{d.note}</div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-white font-medium font-mono">₹{d.nav.toFixed(4)}</span>
                  {next ? (
                    <span className={`text-xs font-mono w-20 text-right ${diff >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {diff >= 0 ? "+" : ""}
                      {diff.toFixed(4)}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-600 w-20 text-right">—</span>
                  )}
                  {isAdmin && isDaily && (
                    <button
                      type="button"
                      onClick={() => handleDeleteDailyNav(d.date)}
                      data-testid={`delete-daily-nav-${d.date}`}
                      title="Remove this manual NAV"
                      className="p-1 rounded text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
